// ============================================
// ProductTranslationsModal
// Nhập bản dịch cho dữ liệu sản phẩm (sheet 2 — mục #15)
// ============================================
//
// GET/PUT /products/{id}/translations/ — body PUT là MẢNG TRẦN, không bọc
// object.
//
// Điều dễ hiểu nhầm nhất về màn này: nó KHÔNG liên quan tới việc hiển thị.
// `GET /products/` đã tự trả tên theo `Accept-Language`, và tìm kiếm cũng quét
// trong bản dịch — nên không có chỗ nào ở FE phải tự ghép tên đa ngôn ngữ.
// Đây thuần tuý là màn nhập liệu cho người vận hành.
//
// Luôn gửi ĐỦ ba ngôn ngữ hệ thống đang hỗ trợ, kể cả ngôn ngữ để trống. BE
// chưa nói rõ "thiếu một language_code trong mảng" nghĩa là giữ nguyên hay xoá
// bản dịch đó, nên FE không đặt cược vào một trong hai cách hiểu.
//
// BỐ CỤC: gom theo TRƯỜNG, mỗi trường liệt kê đủ ba ngôn ngữ — không dùng tab
// ngôn ngữ. Bản 08/09 dùng tab và hỏng ở hai chỗ: (1) dịch là việc SO SÁNH,
// mà tab giấu hai phần ba nội dung nên không đối chiếu được với nhau hay với
// bản gốc; (2) khi sản phẩm chưa có bản dịch nào thì mọi tab đều trống, bấm
// qua lại không thấy gì đổi và trông như giao diện hỏng. Chín ô nhập bày hết
// ra vẫn vừa một màn cuộn, và không giấu trạng thái nào cả.

import { useEffect, useState } from 'react';
import type { ProductTranslation } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import { useProductTranslations, useUpsertProductTranslations } from '../../hooks';

/**
 * Ngôn ngữ store-admin quản lý — khớp với `i18n/locales/`.
 *
 * BE chỉ nhận 4 mã: `pl`, `vi`, `en`, `uk` (xác nhận 18/09/2026); mã ngoài
 * danh sách là 400. Ba mã dưới đây đều hợp lệ, nên KHÔNG có ô nào ở màn này
 * từng gửi mã sai — thêm mã mới thì phải nằm trong 4 mã đó.
 *
 * `vi` cố ý CHƯA thêm: giao diện store-admin chỉ có en/pl/uk, thêm một ô tiếng
 * Việt vào đây là thêm việc dịch cho một ngôn ngữ chưa ai đọc ở thị trường
 * này. Bật lên khi vận hành thật sự cần (đã hỏi lại BE/PO).
 */
const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'uk', label: 'Українська' },
];

interface ProductTranslationsModalProps {
  productId: string;
  productName: string;
  onClose: () => void;
}

/**
 * `unit_label` KHÔNG còn ô nhập nào (bỏ 09/09 — màn này chỉ dịch tên và mô tả),
 * nhưng vẫn nằm trong draft và vẫn được gửi lại NGUYÊN VẸN.
 *
 * Lý do: PUT /translations/ ghi đè cả dòng. Bỏ hẳn field ra khỏi payload, hoặc
 * gửi `null`, là xoá sạch nhãn đơn vị mà người khác đã nhập trước đó — mà lần
 * này còn xoá âm thầm, chỉ vì có người mở màn dịch ra sửa mỗi cái tên.
 */
type Draft = Record<string, { name: string; short_description: string; unit_label: string }>;

const emptyDraft = (): Draft =>
  Object.fromEntries(
    LANGUAGES.map((l) => [l.code, { name: '', short_description: '', unit_label: '' }])
  );

const inputCls = (invalid = false) =>
  `h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 ${
    invalid
      ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-200'
      : 'border-slate-300 focus:border-primary-400 focus:ring-primary-200'
  }`;

export function ProductTranslationsModal({
  productId,
  productName,
  onClose,
}: ProductTranslationsModalProps) {
  const { t } = useI18n();
  const { data, isLoading, error } = useProductTranslations(productId);
  const { mutate: save, isPending } = useUpsertProductTranslations();

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  /** Lỗi tự kiểm ở FE (không phải lỗi mạng) — hiện ngay trong modal. */
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Ngôn ngữ trước đó CÓ bản dịch mà người dùng vừa xoá trắng ô tên.
   *
   * Những dòng này sẽ bị XOÁ khi lưu (BE 18/09/2026: name rỗng = xoá). Xoá là
   * thao tác không lùi được nên nói trước, thay vì để người dùng phát hiện sau
   * khi modal đã đóng.
   */
  const clearedLanguages = LANGUAGES.filter(
    (l) =>
      !draft[l.code].name.trim() &&
      (data ?? []).some((row) => row.language_code === l.code && (row.name ?? '').trim())
  );

  /**
   * Ngôn ngữ đã gõ mô tả (hoặc nhãn đơn vị) nhưng BỎ TRỐNG Ô TÊN.
   *
   * `name` là thứ quyết định một bản dịch có tồn tại hay không — BE coi tên
   * rỗng là "xoá bản dịch này", nên một dòng chỉ có mô tả thì không có chỗ nào
   * để lưu mô tả đó vào. Trước đây `handleSave` lặng lẽ bỏ qua những dòng như
   * vậy: người dùng gõ xong một đoạn mô tả tiếng Ba Lan, bấm Lưu, modal đóng
   * lại như thể mọi thứ đều ổn, và đoạn vừa gõ biến mất không dấu vết.
   *
   * Mất dữ liệu trong im lặng là kiểu lỗi tệ nhất ở một form nhập liệu, nên
   * chặn hẳn ở `handleSave` thay vì chỉ cảnh báo: bắt người dùng hoặc điền nốt
   * tên, hoặc tự xoá mô tả — cả hai đều là quyết định của họ, không phải của
   * máy.
   */
  const incompleteLanguages = LANGUAGES.filter(
    (l) =>
      !draft[l.code].name.trim() &&
      (draft[l.code].short_description.trim() || draft[l.code].unit_label.trim())
  );

  // Nạp bản dịch đã có vào draft. Ngôn ngữ chưa có bản dịch để TRỐNG.
  //
  // KHÔNG mượn tên gốc điền vào ô nào cả. Bản 09/09 có điền sẵn tên gốc vào
  // ngôn ngữ giao diện hiện tại, và cách đó sai: ngôn ngữ giao diện lúc MỞ
  // modal không liên quan gì tới ngôn ngữ lúc TẠO sản phẩm — người mở thường
  // không phải người tạo. Kết quả là tên tiếng Ba Lan bị chép vào ô English
  // rồi lưu thành một bản dịch tiếng Anh giả.
  //
  // Ngôn ngữ gốc giờ được ghi ngay lúc tạo (`useCreateProduct`), nên sản phẩm
  // tạo mới đã có sẵn dòng đúng ngôn ngữ và không cần đoán nữa. Sản phẩm cũ
  // để trống — trống là sự thật, còn đoán thì tạo ra dữ liệu sai.
  useEffect(() => {
    if (!data) return;
    const next = emptyDraft();
    for (const row of data) {
      if (!next[row.language_code]) continue;
      next[row.language_code] = {
        name: row.name ?? '',
        short_description: row.short_description ?? '',
        unit_label: row.unit_label ?? '',
      };
    }
    setDraft(next);
  }, [data]);

  const setField = (lang: string, field: keyof Draft[string], value: string) => {
    setDraft((prev) => ({ ...prev, [lang]: { ...prev[lang], [field]: value } }));
    // Gõ tiếp = đang sửa. Giữ nguyên khung đỏ của lần submit trước trong khi
    // người dùng đã bắt đầu vá chỗ sai chỉ làm họ tưởng vẫn còn lỗi.
    setSaveError(null);
  };

  /**
   * Chỉ gửi những ngôn ngữ ĐÃ CÓ tên dịch (bug #237).
   *
   * Trước đây hàm này gửi cả ba ngôn ngữ trong mọi trường hợp, kể cả ngôn ngữ
   * để trống, với `name: ''`. Nhưng `name` là field bắt buộc của serializer BE,
   * nên một ô tên bỏ trống — chuyện hoàn toàn bình thường, vì có ai bắt phải
   * dịch đủ ba thứ tiếng đâu — lại khiến cả request 400 "Validation Failed".
   * Kết quả là không lưu nổi gì cho tới khi điền kín chín ô, trong đó có cả
   * những ô vốn không bắt buộc.
   *
   * Lọc ở đây là đúng chỗ: một dòng không có tên thì không phải là bản dịch,
   * gửi lên cũng chỉ để bị từ chối.
   *
   * CÒN TREO (cần BE, xem note cuối): xoá một bản dịch đã có. Bỏ ngôn ngữ ra
   * khỏi mảng hiện chưa rõ là BE hiểu "giữ nguyên" hay "xoá" — nên màn này báo
   * trước cho người dùng thay vì im lặng làm một trong hai.
   */
  /**
   * Gửi ĐỦ ba ngôn ngữ đang hiện, kể cả ngôn ngữ để trống (bug #237).
   *
   * Quy tắc BE chốt 18/09/2026:
   *   language_code + name có chữ  -> tạo / cập nhật
   *   language_code + name rỗng    -> XOÁ bản dịch đó
   *   không gửi language_code      -> giữ nguyên, không đụng tới
   *
   * Đây KHÔNG phải full replace: gửi một ngôn ngữ không xoá hai ngôn ngữ kia.
   * Nhờ vậy bản lọc-bỏ-ngôn-ngữ-trống của vòng trước đã gỡ được — nó vốn là
   * cách chữa cháy cho việc BE bắt buộc `name`, và cái giá là xoá trắng một ô
   * tên thì bản dịch cũ vẫn nằm im trong DB.
   *
   * Giờ ô trống diễn đạt đúng ý người dùng: xoá.
   */
  const handleSave = () => {
    /**
     * Gửi những ngôn ngữ CÓ VIỆC PHẢI LÀM, bỏ qua dòng không thay đổi gì.
     *
     * Ba nhóm:
     *   - có tên          -> tạo / cập nhật
     *   - trống, trước CÓ -> xoá (BE: name rỗng = xoá)
     *   - trống, trước cũng trống -> KHÔNG gửi
     *
     * Nhóm thứ ba mới là điểm đáng nói. Theo contract mới thì gửi hay không
     * gửi đều cho cùng kết quả ("không gửi language_code = giữ nguyên", mà
     * giữ nguyên một ô vốn đã trống thì vẫn trống). Nhưng bản BE có quy tắc
     * xoá ĐANG Ở NHÁNH LOCAL, chưa lên QA — QA hiện tại vẫn bắt `name` bắt
     * buộc cho MỌI phần tử trong mảng.
     *
     * Nên nếu gửi cả dòng trống vô nghĩa, mọi sản phẩm chưa dịch đủ ba thứ
     * tiếng sẽ 400 ngay trên QA: đúng bug #237 quay lại, lần này do FE gây ra.
     * Lọc nhóm ba đi thì thu hẹp bề mặt 400 về đúng thao tác XOÁ — vốn là
     * tính năng mới, chưa ai dùng trước khi BE lên QA.
     *
     * Sau khi BE lên QA, giữ nguyên cách này cũng không sao: bỏ dòng no-op là
     * đúng về mặt payload, không phải cách chữa cháy.
     */
    // Chặn trước khi dựng payload — xem ghi chú ở `incompleteLanguages`.
    if (incompleteLanguages.length > 0) {
      setSaveError(
        t('translationsIncompleteError', {
          languages: incompleteLanguages.map((l) => l.label).join(', '),
        })
      );
      return;
    }

    const payload: ProductTranslation[] = LANGUAGES.filter((l) => {
      if (draft[l.code].name.trim()) return true;
      // Trống: chỉ gửi khi đây là yêu cầu XOÁ một bản dịch đang tồn tại.
      return clearedLanguages.some((c) => c.code === l.code);
    }).map((l) => ({
      language_code: l.code,
      // Rỗng ở đây luôn có nghĩa là "xoá" — dòng trống vô nghĩa đã lọc ở trên.
      name: draft[l.code].name.trim(),
      short_description: draft[l.code].short_description.trim() || null,
      // Gửi lại y nguyên giá trị đọc về — xem ghi chú ở `Draft`.
      unit_label: draft[l.code].unit_label.trim() || null,
    }));

    // Không có gì để gửi (mở ra rồi đóng lại, không sửa gì) — đừng gọi API.
    if (payload.length === 0) {
      onClose();
      return;
    }

    setSaveError(null);
    save({ productId, translations: payload }, { onSuccess: onClose });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('translationsTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{productName}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-slate-500">{t('loading')}</p>
          ) : (
            <div className="space-y-5">
              {error && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error instanceof Error ? error.message : t('translationsLoadFailed')}
                </div>
              )}

              {saveError && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</div>
              )}

              <p className="text-xs text-slate-500">{t('translationsHint')}</p>

              {/* Ngôn ngữ để trống là hợp lệ — không bắt điền đủ (bug #237). */}
              <p className="text-xs text-slate-400">{t('translationsOptionalHint')}</p>

              {/* Báo NGAY lúc gõ, không đợi bấm Lưu: người dùng đang ở đúng ô
                  đó, sửa một nhịp là xong. Để tới lúc submit thì họ đã chuyển
                  sự chú ý sang chỗ khác. */}
              {incompleteLanguages.length > 0 && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t('translationsIncompleteWarning', {
                    languages: incompleteLanguages.map((l) => l.label).join(', '),
                  })}
                </div>
              )}

              {clearedLanguages.length > 0 && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  {t('translationsWillDeleteWarning', {
                    languages: clearedLanguages.map((l) => l.label).join(', '),
                  })}
                </div>
              )}

              {/* ── Tên sản phẩm ─────────────────────────────────────────── */}
              <section>
                <h4 className="mb-1 text-sm font-bold text-slate-900">{t('productName')}</h4>
                {/* Bản gốc luôn nằm ngay trên các ô dịch. Không có nó thì người
                    dịch phải cuộn lên đọc tiêu đề modal rồi cuộn xuống gõ. */}
                <p className="mb-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  <span className="mr-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    {t('translationsOriginal')}
                  </span>
                  {productName}
                </p>
                <div className="space-y-2">
                  {LANGUAGES.map((l) => (
                    <div key={l.code} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-sm font-semibold text-slate-600">
                        {l.label}
                      </span>
                      <input
                        value={draft[l.code].name}
                        onChange={(e) => setField(l.code, 'name', e.target.value)}
                        placeholder={t('translationsNotTranslated')}
                        className={inputCls(incompleteLanguages.some((i) => i.code === l.code))}
                      />
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Mô tả ngắn ───────────────────────────────────────────── */}
              <section>
                <h4 className="mb-2 text-sm font-bold text-slate-900">
                  {t('translationsShortDescription')}
                </h4>
                <div className="space-y-2">
                  {LANGUAGES.map((l) => (
                    <div key={l.code} className="flex items-start gap-3">
                      <span className="w-24 shrink-0 pt-2 text-sm font-semibold text-slate-600">
                        {l.label}
                      </span>
                      <textarea
                        value={draft[l.code].short_description}
                        onChange={(e) => setField(l.code, 'short_description', e.target.value)}
                        rows={2}
                        placeholder={t('translationsNotTranslated')}
                        className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  ))}
                </div>
              </section>

            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isPending || isLoading}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? t('saving') : t('saveBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
